import { Trash2, MonitorOff, ChevronDown, ChevronUp, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import {
  fileFiltersBoxClassName,
  filterComboboxClassName,
  filterSearchClassName,
  tableWrapperClassName,
} from "../common/Table/TableStyles";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import TableEmptyState from "../common/TableEmptyState";
import { cn } from "@/lib/utils";
import * as React from "react";
import {useState} from "react";
import {useQuery} from "@tanstack/react-query";
import ClientInfoRepository from "../../repositories/ClientInfoRepository";
import {UserRole} from "../../models/User";
import {QUERY_KEYS} from "../../constants/QueryKeys";
import {QUERY_CONFIG} from "../../constants/QueryConfig";
import {ClientConfigSelector} from "./ClientConfigSelector";
import {ClientInfo} from "../../models/Client/ClientInfo";
import {HealthStatusChip} from "./HealthStatusChip";
import {DependencyHealthStatus} from "../../models/Client/DependencyHealthStatus";
import {ClientStatus, ClientStatusColors, ClientStatusIcons, ClientStatusLabels} from "../../models/Client/ClientStatus";
import {VersionInfoChip} from "./VersionInfoChip";
import {FilterCombobox} from "../common/FilterCombobox";
import TablePagination from "../common/Table/TablePagination";
import { BlacklistedVersionsCell } from "./BlacklistedVersionsCell";
import { CacheSettingsCell } from "./CacheSettingsCell";
import ActionConfirmationModal, { ACTION_CONFIGS } from "../common/ActionConfirmationModal";

interface ClientsTableProps {
  organizationId: string;
  onConfigChange: (clientId: string, newConfigId: string | null, reason?: string) => Promise<void> | void;
  onCacheSettingsChange: (clientId: string, useCache: boolean, useDeltas: boolean, rebuildCache: boolean, reason?: string) => Promise<void> | void;
  onSoftDeleteClient: (clientId: string, clientName: string) => void;
  onForceRemoveClient: (clientId: string, clientName: string) => void;
  onUninstallClient: (clientId: string, clientName: string) => void;
  onCancelUninstallClient: (clientId: string, clientName: string) => void;
  onResetBlacklistedVersion: (organizationId: string, clientId: string, version: string, applicationType: string) => void;
  userRole: UserRole;
  isAssignedUser: boolean;
}

export const ClientsTable: React.FC<ClientsTableProps> = ({
  organizationId,
  onConfigChange,
  onCacheSettingsChange,
  onSoftDeleteClient,
  onForceRemoveClient,
  onUninstallClient,
  onCancelUninstallClient,
  onResetBlacklistedVersion,
  userRole,
  isAssignedUser,
}) => {
  const clientInfoRepository = new ClientInfoRepository();
  const { data: clientInfos = [], error, isLoading } = useQuery<ClientInfo[]>({
    queryKey: [QUERY_KEYS.CLIENT_INFOS, organizationId],
    queryFn: () => clientInfoRepository.getClientInfos(organizationId),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
    refetchIntervalInBackground: true,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [collapsedHealthChecks, setCollapsedHealthChecks] = React.useState<Set<string>>(new Set());
  const toggleHealthChecks = (clientId: string) => {
    setCollapsedHealthChecks((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const [pendingConfigChange, setPendingConfigChange] = useState<{ clientId: string; newConfigId: string | null } | null>(null);
  const [pendingCacheSettingsChange, setPendingCacheSettingsChange] = useState<{ clientId: string; useCache: boolean; useDeltas: boolean; rebuildCache: boolean } | null>(null);

  const handleConfigChangeInternal = async (clientId: string, newConfigId: string | null) => {
    if (userRole === UserRole.Editor) {
      setPendingConfigChange({ clientId, newConfigId });
      return;
    }
    // Direct (non-modal) Admin path — no ActionConfirmationModal wrapping it,
    // so toast feedback must happen here.
    try {
      await onConfigChange(clientId, newConfigId);
      toast.success("Client active configuration changed");
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to change client configuration");
    }
  };

  const handleCacheSettingsSave = async (clientInfo: ClientInfo, settings: { useCache: boolean; useDeltas: boolean; rebuildCache: boolean }) => {
    if (userRole === UserRole.Editor) {
      setPendingCacheSettingsChange({ clientId: clientInfo.id, ...settings });
      return;
    }
    try {
      await onCacheSettingsChange(clientInfo.id, settings.useCache, settings.useDeltas, settings.rebuildCache);
      toast.success("Client cache settings changed");
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to change client cache settings");
    }
  };

  const shouldShowDependencyAsOnline = (lastDependencyHealthCheckUtc: string): boolean => {
    const lastCheckTime = new Date(lastDependencyHealthCheckUtc);
    const now = new Date();
    const timeDifferenceMs = now.getTime() - lastCheckTime.getTime();
    const timeDifferenceMinutes = timeDifferenceMs / (1000 * 60);
    return timeDifferenceMinutes <= 75;
  };

  if (error) {
    return (
      <span>
        Error: {(error as { message?: string })?.message || "Unknown error"}
      </span>
    );
  }

  if (!clientInfos)
    return <></>;

  const filtered = clientInfos
    .filter((c) => !searchQuery || c.clientCode?.toLowerCase().includes(searchQuery.toLowerCase()) || c.id.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter((c) => !statusFilter || c.status === statusFilter);

  const hasAnyClientBlacklist = clientInfos.some((c) => c.clientBlacklistedVersions && c.clientBlacklistedVersions.length > 0);
  const hasAnyUpdaterBlacklist = clientInfos.some((c) => c.updaterBlacklistedVersions && c.updaterBlacklistedVersions.length > 0);

  const totalRows = filtered.length;
  const paginatedClients = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handleRowsPerPageChange = (newValue: number) => {
    setRowsPerPage(newValue);
    setPage(0);
  };

  return (
    <>
      <ActionConfirmationModal
        actionConfig={ACTION_CONFIGS.CHANGE_CLIENT_ACTIVE_CONFIGURATION}
        open={pendingConfigChange !== null}
        onClose={() => setPendingConfigChange(null)}
        onConfirm={async (reason) => {
          if (pendingConfigChange) {
            // Await so ActionConfirmationModal's success/error toasts reflect
            // the actual mutation outcome.
            await onConfigChange(pendingConfigChange.clientId, pendingConfigChange.newConfigId, reason);
          }
          setPendingConfigChange(null);
        }}
      />
      <ActionConfirmationModal
        actionConfig={ACTION_CONFIGS.CHANGE_CLIENT_CACHE_SETTINGS}
        open={pendingCacheSettingsChange !== null}
        onClose={() => setPendingCacheSettingsChange(null)}
        onConfirm={async (reason) => {
          if (pendingCacheSettingsChange) {
            const { clientId, useCache, useDeltas, rebuildCache } = pendingCacheSettingsChange;
            await onCacheSettingsChange(clientId, useCache, useDeltas, rebuildCache, reason);
          }
          setPendingCacheSettingsChange(null);
        }}
      />
      <div className="flex flex-col w-full flex-1 min-h-0">
        <div className={fileFiltersBoxClassName}>
          <div className={filterSearchClassName}>
            <Label className="text-xs font-semibold text-muted-foreground">Search clients</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Client code or ID"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              />
            </div>
          </div>
          <div className={filterComboboxClassName}>
            <Label className="text-xs font-semibold text-muted-foreground">Status</Label>
            <FilterCombobox
              value={statusFilter}
              onChange={(newValue) => { setStatusFilter(newValue); setPage(0); }}
              placeholder="Status"
              options={Object.values(ClientStatus).map((status) => ({
                value: status,
                label: ClientStatusLabels[status],
                color: ClientStatusColors[status],
                icon: ClientStatusIcons[status],
              }))}
            />
          </div>
        </div>
        <div className={cn(tableWrapperClassName, (error || isLoading) && "opacity-50 pointer-events-none")}>
          <Table containerClassName="flex-1 min-h-0">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[12%] px-1.5 py-3">
                  Client Id
                </TableHead>
                <TableHead className="w-[12%] px-1.5 py-3">
                  Client Code
                </TableHead>
                <TableHead className="w-[8%] px-1.5 py-3">
                  Status
                </TableHead>
                <TableHead className="w-[20%] px-1.5 py-3">
                  Health Checks
                </TableHead>
                {hasAnyClientBlacklist && (
                  <TableHead className="w-[10%] px-1.5 py-3">
                    Client Ver. Blacklist
                  </TableHead>
                )}
                {hasAnyUpdaterBlacklist && (
                  <TableHead className="w-[10%] px-1.5 py-3">
                    Updater Ver. Blacklist
                  </TableHead>
                )}
                <TableHead className="w-[10%] px-1.5 py-3">
                  Cache Settings
                </TableHead>
                <TableHead className="w-[14%] px-1.5 py-3">
                  Active Configuration
                </TableHead>
                <TableHead className="w-[95px] px-1.5 py-3" />
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedClients.map((clientInfo: ClientInfo) => {
                return (
                  <TableRow key={clientInfo.id}>
                    <TableCell className="whitespace-normal break-all">
                      <span className="text-sm">
                        {clientInfo.id}
                      </span>
                    </TableCell>
                    <TableCell className="whitespace-normal break-all">
                      <span className="text-sm">
                        {clientInfo.clientCode}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={ClientStatusColors[clientInfo.status]}>
                        {(() => { const Icon = ClientStatusIcons[clientInfo.status]; return Icon ? <Icon className="size-3" /> : null; })()}
                        {ClientStatusLabels[clientInfo.status]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                          <HealthStatusChip
                            status={
                              clientInfo.isActive && shouldShowDependencyAsOnline(clientInfo.nfsApiDependencyLastHealthCheckUtc)
                                ? clientInfo.nfsApiDependencyHealthStatus
                                : DependencyHealthStatus.Offline
                            }
                            lastHealthCheckUtc={clientInfo.nfsApiDependencyLastHealthCheckUtc}
                            label="NFS API"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 shrink-0"
                            onClick={() => toggleHealthChecks(clientInfo.id)}
                            aria-label={collapsedHealthChecks.has(clientInfo.id) ? "Expand health checks" : "Collapse health checks"}
                          >
                            {collapsedHealthChecks.has(clientInfo.id) ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
                          </Button>
                        </div>
                        {!collapsedHealthChecks.has(clientInfo.id) && (
                          <>
                            <HealthStatusChip
                              status={
                                clientInfo.isActive && shouldShowDependencyAsOnline(clientInfo.onPremDatabaseDependencyLastHealthCheckUtc)
                                  ? clientInfo.onPremDatabaseDependencyHealthStatus
                                  : DependencyHealthStatus.Offline
                              }
                              lastHealthCheckUtc={clientInfo.onPremDatabaseDependencyLastHealthCheckUtc}
                              label="On-prem Database"
                            />
                            <HealthStatusChip
                              status={
                                clientInfo.isActive
                                  ? clientInfo.sharepointDependencyHealthStatus
                                  : DependencyHealthStatus.Offline
                              }
                              lastHealthCheckUtc={clientInfo.sharepointDependencyLastHealthCheckUtc}
                              label="SharePoint"
                            />
                            <HealthStatusChip
                              status={
                                clientInfo.isActive && shouldShowDependencyAsOnline(clientInfo.lokiDependencyLastHealthCheckUtc)
                                  ? clientInfo.lokiDependencyHealthStatus
                                  : DependencyHealthStatus.Offline
                              }
                              lastHealthCheckUtc={clientInfo.lokiDependencyLastHealthCheckUtc}
                              label="Loki"
                            />
                            <VersionInfoChip
                              version={clientInfo.clientVersionHealthStatus}
                              lastHealthCheckUtc={clientInfo.clientVersionLastHealthCheckUtc}
                              label="Client Version"
                              component="client"
                            />
                            <VersionInfoChip
                              version={clientInfo.updaterVersionHealthStatus}
                              lastHealthCheckUtc={clientInfo.updaterVersionLastHealthCheckUtc}
                              label="Updater Version"
                              component="updater"
                            />
                          </>
                        )}
                      </div>
                    </TableCell>
                    {hasAnyClientBlacklist && (
                      <TableCell>
                        <BlacklistedVersionsCell
                          canEdit={userRole !== UserRole.Viewer && (isAssignedUser || userRole === UserRole.Admin)}
                          versions={clientInfo.clientBlacklistedVersions}
                          onDelete={(version) => onResetBlacklistedVersion(organizationId, clientInfo.id, version, "Client")}
                        />
                      </TableCell>
                    )}
                    {hasAnyUpdaterBlacklist && (
                      <TableCell>
                        <BlacklistedVersionsCell
                          canEdit={userRole !== UserRole.Viewer && (isAssignedUser || userRole === UserRole.Admin)}
                          versions={clientInfo.updaterBlacklistedVersions}
                          onDelete={(version) => onResetBlacklistedVersion(organizationId, clientInfo.id, version, "Updater")}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <CacheSettingsCell
                        value={{ useCache: clientInfo.useCache, useDeltas: clientInfo.useDeltas, rebuildCache: clientInfo.rebuildCache }}
                        onSave={(settings) => handleCacheSettingsSave(clientInfo, settings)}
                        disabled={
                          userRole === UserRole.Viewer
                          || clientInfo.status === ClientStatus.PendingUninstall
                          || clientInfo.status === ClientStatus.Uninstalled
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <ClientConfigSelector
                        disabled={
                          userRole === UserRole.Viewer
                          || clientInfo.status === ClientStatus.PendingUninstall
                          || clientInfo.status === ClientStatus.Uninstalled
                        }
                        organizationId={organizationId}
                        activeConfigId={clientInfo.activeConfiguration}
                        onConfigChange={(newConfigId) => handleConfigChangeInternal(clientInfo.id, newConfigId)}
                      />
                    </TableCell>
                    <TableCell>
                      {clientInfo.status === ClientStatus.PendingUninstall ? (
                        <Button
                          disabled={
                            userRole === UserRole.Viewer
                            || (!isAssignedUser && userRole !== UserRole.Admin)
                          }
                          aria-label="Cancel uninstall"
                          size="sm"
                          variant="outline"
                          onClick={() => onCancelUninstallClient(clientInfo.id, clientInfo.clientCode)}
                        >
                          Cancel uninstall
                        </Button>
                      ) : (
                        <Button
                          disabled={
                            userRole === UserRole.Viewer
                            || (!isAssignedUser && userRole !== UserRole.Admin)
                            || clientInfo.status === ClientStatus.Uninstalled
                          }
                          aria-label="Uninstall"
                          size="sm"
                          variant="secondary"
                          onClick={() => onUninstallClient(clientInfo.id, clientInfo.clientCode)}
                        >
                          Uninstall
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      {clientInfo.status === ClientStatus.Uninstalled ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-destructive"
                                disabled={
                                  userRole === UserRole.Viewer
                                  || (!isAssignedUser && userRole !== UserRole.Admin)
                                }
                                aria-label="SoftDelete"
                                onClick={() => onSoftDeleteClient(clientInfo.id, clientInfo.clientCode)}
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Soft Delete</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-destructive"
                                disabled={
                                  userRole === UserRole.Viewer
                                  || (!isAssignedUser && userRole !== UserRole.Admin)
                                }
                                aria-label="ForceRemove"
                                onClick={() => onForceRemoveClient(clientInfo.id, clientInfo.clientCode)}
                              >
                                <MonitorOff className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Force Remove</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {paginatedClients.length === 0 && !isLoading && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={99}>
                    <TableEmptyState title="No clients found" />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            rows={totalRows}
            onPageChange={setPage}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </div>
      </div>
    </>
  );
};
